import React from "react";
import { type NextPage } from "next";
import { Page } from "components/page";
import { getLandingPageProps, getPageMetaImage, type LandingPageProps } from "utils";
import { StackedLogos } from "components/logos";
import { CenteredQuote } from "components/quotes";
import { Section, sectionSpacingY } from "components/section";
import { IdrHero } from "components/platform/identity-resolution/idr-hero";
import { IdentitiesExplainer } from "components/platform/identity-resolution/identities-explainer";
import { IdrProductFeatures } from "components/platform/identity-resolution/idr-product-features";
import { ComposableCdpFeaturesPlate, DestinationMarquee } from "components/content";

const IdentityResolution: NextPage<LandingPageProps> = ({ page, announcementBanner, destinations, defaultLogos }) => {
  return (
    <Page
      theme="light"
      bgColor="new.blue.150"
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      footerOptions={{ variant: "blue" }}
    >
      <IdrHero eyebrow={page.eyebrow} title={page.title} description={page.description} />

      <StackedLogos customers={defaultLogos} my="0" pt={{ base: "big", md: "huge" }} pb="0" />

      <Section bg="white">
        <CenteredQuote
          maxW="4xl"
          author={{
            name: "Rachel Bradley-Haas",
            job: "Co-Founder & CEO",
          }}
          company={{
            name: "Big Time Data",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/cf1411ab20b2c4730fe08f57f7596798a8342cc3-356x141.png",
            logoRatio: 356 / 141,
          }}
          quoteText="Hightouch is empowering companies to self-serve identity and entity resolution, without writing code. This is a game-changer and puts Hightouch in a league of its own for data management platforms."
        />
      </Section>

      <IdentitiesExplainer />

      <IdrProductFeatures />

      <ComposableCdpFeaturesPlate
        theme="dark"
        bgColor="new.blue.900"
        title="Resolve identities in your data warehouse, not a black box"
        description="Traditional Customer Data Platforms perform identity resolution within their own rigid systems. You don’t have control, and you have to store your data with them. Instead, Hightouch unifies customer profiles directly in your data warehouse."
      />

      <DestinationMarquee destinations={destinations} my={0} py={sectionSpacingY} />
    </Page>
  );
};

export default IdentityResolution;

export const getStaticProps = getLandingPageProps("/platform/identity-resolution", {
  loadDefaultLogos: true,
  loadDestinations: "all",
});
